/**
 * XYLink WebRTC SDK Queue Task Lib
 *
 * Created at     : 2023-04-19 09:46:28
 * Last modified  : 2023-09-21 16:16:18
 */

type AsyncFn<T> = (...args: any[]) => Promise<T>;

type Fn = (...args: any) => void;

type QueueItem<T> = { task: AsyncFn<T>; resolve: Fn; reject: Fn };

class Queue<T> {
  // 是否进行中
  private isRunning;
  // 结束回调函数
  private taskCompletedCB: (() => void) | null;
  // 任务队列
  private waitingQueue: QueueItem<T>[];

  constructor() {
    this.waitingQueue = [];
    this.isRunning = false;
    this.taskCompletedCB = null;
  }

  /**
   * 监听任务执行完成状态
   *
   * @param { (() => void) | null } callback - 任务结束回调
   */
  public onTaskCompleted(callback: () => void) {
    this.taskCompletedCB = callback;
  }

  /**
   * 添加同步任务
   *
   * @param { AsyncFn<T> } task - 同步任务函数
   * @returns { Promise<T> }
   */
  public addTask(task: AsyncFn<T>): Promise<T> {
    const _this = this;

    return new Promise((resolve, reject) => {
      if (_this.isRunning) {
        _this.waitingQueue.push({ task, resolve, reject });
      } else {
        _this.isRunning = true;
        _this.execute(task, resolve, reject);
      }
    });
  }

  /**
   * 执行同步任务队列
   *
   * @param { AsyncFn<T> } task - 同步任务函数
   * @param { Fn } resolve - Resolve函数
   * @param { Fn } reject - Reject函数
   */
  private execute(task: AsyncFn<T>, resolve: Fn, reject: Fn) {
    task()
      .then((data) => resolve(data))
      .catch((err) => reject(err))
      .finally(() => {
        if (this.waitingQueue.length) {
          const next = this.waitingQueue.shift();

          if (next) {
            this.execute(next.task, next.resolve, next.reject);
          }
        } else {
          this.isRunning = false;

          if (this.taskCompletedCB) {
            this.taskCompletedCB();
          }
        }
      });
  }

  /**
   * 获取执行中的任务数量
   *
   * @returns { number }
   */
  public taskLength() {
    return this.waitingQueue.length;
  }

  /**
   * 清空任务队列
   */
  public clear() {
    this.waitingQueue = [];
    this.isRunning = false;
  }
}

export default Queue;
